import { useState, type ReactNode, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { EventsSidebar } from './EventsSidebar';
import { OnboardingTour } from './OnboardingTour';
import { BetaBadge } from './BetaBadge';
import { hapticLight } from '../capacitorInit';

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout, delegatedAccounts, activeAccountId, setActiveAccountId } = useAuth();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery('');
    setSearchOpen(false);
  }

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  function handleAccountSwitch(id: string | null) {
    setActiveAccountId(id);
    setSwitcherOpen(false);
    window.location.reload();
  }

  const activeAccount = activeAccountId
    ? delegatedAccounts.find(d => d.account.id === activeAccountId)
    : null;

  const currentLabel = activeAccount
    ? `${activeAccount.account.owner.name}'s archive`
    : 'My archive';

  return (
    <div className="min-h-screen bg-[#F8F9FF]">
      <header className="bg-white border-b border-indigo-50 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="px-4 lg:px-6 h-[62px] flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-[34px] h-[34px] rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
              </svg>
            </div>
            <span className="text-[1.05rem] font-bold tracking-tight text-slate-900">EventsCarousel</span>
          </Link>

          {user && (
            <>
              {/* Desktop search */}
              <form onSubmit={handleSearch} className="hidden desktop:flex items-center flex-1 max-w-xs mx-4">
                <div className="relative w-full">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search events, places, people…"
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-colors"
                  />
                </div>
              </form>

              {/* Desktop nav */}
              <nav className="hidden desktop:flex items-center gap-1 text-sm justify-end">
                <Link to="/events" className="px-3.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">My Events</Link>
                <Link to="/account/members" className="px-3.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">My Circle</Link>
                <Link to="/faces" className="px-3.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5">Faces <BetaBadge /></Link>
                <Link to="/about" className="px-3.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">About</Link>
                <Link to="/help" className="px-3.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">Help</Link>
                <div className="w-px h-5 bg-slate-200 mx-2" />

                {delegatedAccounts.length > 0 && (
                  <div className="relative mr-2">
                    <button
                      onClick={() => setSwitcherOpen(o => !o)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors"
                    >
                      <span>{currentLabel}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </button>
                    {switcherOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setSwitcherOpen(false)} />
                        <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50">
                          <button
                            onClick={() => handleAccountSwitch(null)}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${!activeAccountId ? 'text-indigo-700 font-medium bg-indigo-50' : 'text-slate-700 hover:bg-slate-50'}`}
                          >
                            My archive
                          </button>
                          {delegatedAccounts.map(d => (
                            <button
                              key={d.id}
                              onClick={() => handleAccountSwitch(d.account.id)}
                              className={`w-full text-left px-4 py-2 text-sm transition-colors ${activeAccountId === d.account.id ? 'text-indigo-700 font-medium bg-indigo-50' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              {d.account.owner.name}'s archive
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <Link to="/settings" className="w-[34px] h-[34px] rounded-full bg-indigo-50 text-indigo-600 text-sm font-bold flex items-center justify-center hover:bg-indigo-100 transition-colors">
                  {(user.name?.[0] ?? '?').toUpperCase()}
                </Link>
                <button onClick={handleLogout} className="ml-1 px-3 py-1.5 rounded-lg text-slate-400 hover:text-slate-600 text-sm transition-colors">Sign out</button>
              </nav>

              {/* Mobile / tablet right controls */}
              <div className="desktop:hidden flex items-center gap-0.5">
                {/* Mobile search toggle */}
                <button
                  onClick={() => setSearchOpen(o => !o)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                  aria-label="Search"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </button>

                {/* Sidebar filter toggle */}
                <button
                  onClick={() => { hapticLight(); setMobileSidebarOpen(o => !o); }}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                  aria-label="Toggle filters"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="12" y1="18" x2="12" y2="18"/>
                  </svg>
                </button>

                {/* Avatar */}
                <Link to="/settings" className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-indigo-600 text-sm font-bold hover:bg-indigo-50 transition-colors">
                  <span className="w-[34px] h-[34px] rounded-full bg-indigo-50 flex items-center justify-center">
                    {(user.name?.[0] ?? '?').toUpperCase()}
                  </span>
                </Link>

                {/* Hamburger menu */}
                <button
                  onClick={() => { hapticLight(); setMobileMenuOpen(o => !o); }}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                  aria-label="Menu"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Mobile dropdown menu */}
              {mobileMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" style={{ top: 'calc(62px + env(safe-area-inset-top, 0px))' }} onClick={() => setMobileMenuOpen(false)} />
                  <div className="absolute right-0 w-56 bg-white border-b border-slate-100 shadow-lg z-50 py-2 desktop:hidden" style={{ top: 'calc(62px + env(safe-area-inset-top, 0px))' }}>
                    {delegatedAccounts.length > 0 && (
                      <div className="px-4 py-2 border-b border-slate-100 mb-1">
                        <p className="text-xs text-slate-400 mb-1.5">Switch archive</p>
                        <button
                          onClick={() => { handleAccountSwitch(null); setMobileMenuOpen(false); }}
                          className={`block w-full text-left text-sm py-1 ${!activeAccountId ? 'text-indigo-600 font-medium' : 'text-slate-700'}`}
                        >
                          My archive
                        </button>
                        {delegatedAccounts.map(d => (
                          <button
                            key={d.id}
                            onClick={() => { handleAccountSwitch(d.account.id); setMobileMenuOpen(false); }}
                            className={`block w-full text-left text-sm py-1 ${activeAccountId === d.account.id ? 'text-indigo-600 font-medium' : 'text-slate-700'}`}
                          >
                            {d.account.owner.name}'s archive
                          </button>
                        ))}
                      </div>
                    )}
                    <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">About</Link>
                    <Link to="/help" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Help</Link>
                    <Link to="/events" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">My Events</Link>
                    <Link to="/account/members" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">My Circle</Link>
                    <Link to="/faces" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Faces <BetaBadge /></Link>
                    <Link to="/settings" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Settings</Link>
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="block w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-slate-50">
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </header>

      {/* Mobile search bar */}
      {searchOpen && user && (
        <div className="desktop:hidden bg-white border-b border-slate-100 px-4 py-2.5">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search events, places, people…"
              autoFocus
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <button type="submit" className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Go</button>
          </form>
        </div>
      )}

      {user ? (
        <div className="relative">
          {/* Mobile sidebar backdrop — fixed overlay, outside flex so it doesn't affect main width */}
          {mobileSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/30 z-30 desktop:hidden"
              style={{ top: 'calc(62px + env(safe-area-inset-top, 0px))' }}
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* Mobile sidebar — fixed, explicit left:0, NOT inside the flex row.
              Only mounted while open: an always-mounted fixed+transform element
              (even translated fully off-canvas) has been observed to throw off
              WKWebView's viewport-size computation on some iOS versions, clipping
              the whole page's left edge by a few px — see carousel_project memory,
              iOS WKWebView left-edge clipping entry. No animation on open/close
              as a result; matches every other overlay in this file already. */}
          {mobileSidebarOpen && (
            <aside
              className="desktop:hidden fixed left-0 z-40 w-64 overflow-y-auto border-r border-slate-100 bg-white"
              style={{
                top: 'calc(62px + env(safe-area-inset-top, 0px))',
                height: 'calc(100dvh - 62px - env(safe-area-inset-top, 0px))',
              }}
            >
              <EventsSidebar onNavigate={() => setMobileSidebarOpen(false)} />
            </aside>
          )}

          {/* Desktop layout — sidebar + main as flex siblings */}
          <div className="flex">
            <aside
              className="hidden desktop:block w-56 shrink-0 overflow-y-auto border-r border-slate-100 bg-white sticky"
              style={{
                top: 'calc(62px + env(safe-area-inset-top, 0px))',
                height: 'calc(100dvh - 62px - env(safe-area-inset-top, 0px))',
              }}
            >
              <EventsSidebar />
            </aside>

            <main className="flex-1 min-w-0 px-4 py-5 lg:px-8 lg:py-8">
              {children}
            </main>
          </div>
        </div>
      ) : (
        <main className="max-w-6xl mx-auto px-4 lg:px-6 py-8 lg:py-10">{children}</main>
      )}
      <OnboardingTour />
      <footer className="border-t border-slate-100 bg-white py-3 px-6 text-center" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}>
        <p className="text-xs text-slate-400">© {new Date().getFullYear()} EventsCarousel. All rights reserved.</p>
      </footer>
    </div>
  );
}
